import React from 'react';
import { useAuth } from '../context/AuthContext';
import { WelcomeScreen } from './WelcomeScreen';
import { BlockedAccountScreen } from './BlockedAccountScreen';
import { TitleBar } from './TitleBar';
import '../styles/RequireAuth.css';

/**
 * RequireAuth - Route guard component (Web SaaS version)
 *
 * Controls access to protected content based on authentication status:
 * - loading → Shows loading spinner
 * - unauth → Redirects to WelcomeScreen (login-only mode)
 * - blocked → Shows BlockedAccountScreen (inactive account)
 * - auth → Renders children (protected content)
 *
 * In the web version, auth state is managed entirely by AuthContext
 * using MSAL Browser - no IPC events needed.
 */

type AuthGuardStatus = 'loading' | 'unauth' | 'blocked' | 'auth';

interface RequireAuthProps {
  children: React.ReactNode;
}

export const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const { authStatus, isLoading } = useAuth();
  const [guardStatus, setGuardStatus] = React.useState<AuthGuardStatus>('loading');

  // Determine guard status based on auth state
  React.useEffect(() => {
    if (isLoading) {
      setGuardStatus('loading');
      return;
    }

    if (!authStatus?.authenticated) {
      setGuardStatus('unauth');
      return;
    }

    // Check if user account is inactive - redirect to login with error
    if (authStatus.user && authStatus.user.isactive === false) {
      setGuardStatus('unauth'); // Show login screen instead of blocked screen
      return;
    }

    // User is authenticated and active
    setGuardStatus('auth');
  }, [authStatus, isLoading]);

  // Focus management: focus title when status changes
  React.useEffect(() => {
    if (guardStatus === 'blocked') {
      // Focus the blocked screen title
      const title = document.querySelector('.blocked-account-title') as HTMLElement;
      if (title) {
        title.focus();
      }
    }
  }, [guardStatus]);

  // Render based on guard status
  switch (guardStatus) {
    case 'loading':
      return (
        <div className="require-auth-loading" role="status" aria-live="polite">
          <div className="require-auth-spinner" aria-hidden="true" />
          <span className="require-auth-loading-text">Vérification de l'authentification...</span>
        </div>
      );

    case 'unauth':
      return (
        <div className="require-auth-shell">
          <TitleBar />
          <main className="require-auth-main">
            <WelcomeScreen />
          </main>
        </div>
      );

    case 'blocked':
      return (
        <div className="require-auth-shell">
          <TitleBar />
          <main className="require-auth-main">
            <BlockedAccountScreen />
          </main>
        </div>
      );

    case 'auth':
      return <>{children}</>;

    default:
      return null;
  }
};
